import { useState } from 'react';
import styled from 'styled-components';
import { theme, useIsomorphicLayoutEffect } from '@frameio/vapor';
import { useReadyBuffer } from '../../hooks';

const Container = styled.div`
  bottom: 0px;
  left: 0px;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  right: 0px;
  top: 0px;
`;

const CurtainLayer = styled.div<{ $color: string; $gradientAngle: string }>`
  background: linear-gradient(
    ${(p) => p.$gradientAngle}deg,
    transparent 0%,
    ${(p) => p.$color} 50%,
    ${(p) => p.$color} 100%
  );
  background: ${(p) => p.$color};
  bottom: 0px;
  left: 0px;
  position: absolute;
  right: 0px;
  top: 0px;
`;

type CurtainOrientation = 'horizontal' | 'vertical';

type CurtainRevealProps = {
  id?: string;
  hold?: boolean;
  bufferTime?: number;
  zIndex?: number;
  color?: string;
  orientation?: CurtainOrientation;
};

/**
 * Use CurtainReveal to achieve a performant cascading transition when a large part of the
 * screen changes. For a video explanation see:
 * https://app.frame.io/player/d3b61b79-3c81-4bd2-b79e-9ce215e4467d
 *
 * @example
 * ```tsx
 *   <CurtainReveal
 *    bufferTime={500}
 *    hold={isLoading}
 *    id="unique-id"
 *  />
 * ```
 */
export const CurtainReveal = ({
  id,
  hold,
  bufferTime = 250,
  orientation = 'horizontal',
  color = theme.colors.background.base,
  zIndex,
}: CurtainRevealProps) => {
  const TRANSITION_TIME = 1; //sec

  const ready = useReadyBuffer({ id, hold, bufferTime });
  const [done, setDone] = useState(false);

  const gradientAngle = orientation === 'horizontal' ? '90' : '180';

  /** wait for the transition to finish then mark as done to unmount the container */
  useIsomorphicLayoutEffect(() => {
    let timeout: number;
    if (ready) {
      timeout = window.setTimeout(() => {
        setDone(true);
      }, TRANSITION_TIME * 1000);
    } else {
      setDone(false);
    }
    return () => window.clearTimeout(timeout);
  }, [ready]);

  if (done) return null;
  return (
    <Container style={Number.isInteger(zIndex) ? { zIndex } : {}}>
      <CurtainLayer
        $color={color}
        $gradientAngle={gradientAngle}
        style={{
          ...(orientation === 'horizontal' && {
            width: '200%',
            left: 'unset',
            transform: ready ? `translateX(100%)` : `translateX(0)`,
            transformOrigin: '0% 50%',
          }),
          ...(orientation === 'vertical' && {
            height: '200%',
            top: 'unset',
            transform: ready ? `translateY(100%)` : `translateY(0)`,
            transformOrigin: '50% 0%',
          }),
          transition: ready
            ? `transform ${TRANSITION_TIME}s ${theme.transitions.eases.primary}`
            : `transform 0s ${theme.transitions.eases.primary}`,
        }}
      />
    </Container>
  );
};
